import React, { useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { connect } from 'react-redux'
import styled from 'styled-components'
import to from 'await-catch'
import {
	ClinicLogo,
	ClinicLogoContainer,
	FormContainer,
	FormHeader,
	FormTitle
} from '../../../components/ui/form'
import Feedback from '../../../components/feedback'
import { CheckCircle, Info } from '../../../assets/icons'
import { Spinner } from '../../../components/Loader'
import { validateRedirect, getPatientDataAtClinic } from '../../../api'
import { login as loginAction, setPatientDetails } from '../../../actions'
import { PATIENT_GROUP } from '../../../constants'
import ClinicaSantaIsabel from '../../../assets/icons/clinicas/ClinicaSantaIsabel.svg'
import ClinicaDelSol from '../../../assets/icons/clinicas/ClinicaDelSol.svg'
import ClinicaBazterrica from '../../../assets/icons/clinicas/ClinicaBazterrica.svg'
import CentroMedicoBazterica from '../../../assets/icons/clinicas/CentroMedicoBazterica.svg'
import CentroMedicoSantaRita from '../../../assets/icons/clinicas/CentroMedicoSantaRita.svg'
import CentroMedicoSantaIsabel from '../../../assets/icons/clinicas/CentroMedicoSantaIsabel.svg'

const FeedbackContent = styled.div`
	flex: 1;
	width: 100%;
	display: flex;
	align-items: flex-start;
	justify-content: center;
`

const getTokenFromUrl = location => {
	const queryParams = new URLSearchParams(location.search)
	const hashValue = location.hash.startsWith('#') ? location.hash.slice(1) : location.hash
	const hashParams = new URLSearchParams(hashValue)
	return (
		queryParams.get('token') ||
		queryParams.get('access_token') ||
		hashParams.get('token') ||
		hashParams.get('access_token') ||
		''
	)
}

const resolveValidationState = response => {
	const data = response?.data ?? response

	if (typeof data === 'boolean') {
		return data
	}

	if (typeof data === 'object' && data !== null) {
		const flagKey = ['isValid', 'valid', 'success', 'ok'].find(
			key => typeof data[key] === 'boolean'
		)
		if (flagKey) {
			return data[flagKey]
		}
	}

	return true
}

const extractAuthData = response => {
	const data = response?.data ?? response
	if (!data || typeof data !== 'object') {
		return null
	}
	if (Array.isArray(data.roles)) {
		return data
	}
	if (data.user && typeof data.user === 'object' && Array.isArray(data.user.roles)) {
		return data.user
	}
	return null
}

const invokeUrlService = async payload => {
	if (!payload.token) {
		return { status: 'error', authData: null }
	}
	try {
		const response = await validateRedirect(payload.token)
		return {
			status: resolveValidationState(response) ? 'success' : 'error',
			authData: extractAuthData(response)
		}
	} catch (error) {
		return { status: 'error', authData: null }
	}
}

const RedirectPage = ({ loginAction, setPatientDetails }) => {
	const history = useHistory()
	const location = useLocation()
	const [status, setStatus] = useState('processing')

	const loadPatientDetails = async authData => {
		if (!authData?.metadatos) {
			return
		}

		const [err, { data } = {}] = await to(getPatientDataAtClinic(0))
		if (err || !data) {
			return
		}

		const doc = authData.metadatos.find(metadata => metadata.metadato.clave == 'NroDoc')
		const phone = authData.metadatos.filter(metadata => metadata.metadato.clave == 'Telefono')
		const patientERID = authData.metadatos.find(
			metadata => metadata.metadato.clave === 'PacienteERID'
		)?.valor
		const erid =
			authData.metadatos.find(metadata => metadata.metadato.clave === 'ERID')?.valor || null

		setPatientDetails({
			...data,
			numeroDocumento: doc ? doc.valor : null,
			telefono: phone.length ? phone[0].valor : data.telefono,
			pacienteErid: patientERID,
			erid
		})
	}

	const urlPayload = useMemo(() => {
		const queryParams = new URLSearchParams(location.search)
		const query = {}

		for (const [key, value] of queryParams.entries()) {
			query[key] = value
		}

		return {
			token: getTokenFromUrl(location),
			path: location.pathname,
			hash: location.hash,
			query
		}
	}, [location.pathname, location.search, location.hash])

	useEffect(() => {
		let mounted = true
		const processUrl = async () => {
			setStatus('processing')
			const { status: nextStatus, authData } = await invokeUrlService(urlPayload)
			if (nextStatus === 'success') {
				if (authData) {
					loginAction(authData)
				}

				if (authData?.roles?.some(value => value === PATIENT_GROUP)) {
					await loadPatientDetails(authData)
				}
			}

			if (mounted) {
				setStatus(nextStatus)
			}
		}
		processUrl()
		return () => {
			mounted = false
		}
	}, [loginAction, setPatientDetails, urlPayload])

	useEffect(() => {
		if (status !== 'success') {
			return
		}
		const timeoutId = setTimeout(() => {
			console.log('Redirecting to /inicio')
			history.replace('/inicio')
		}, 5000)

		return () => clearTimeout(timeoutId)
	}, [history, status])

	return (
		<FormContainer
			align="center"
			justify="start"
			justifyMobile="start"
			gap={8}
			paddingMobile="30px 10px 10px 0"
		>
			<FormHeader align="center" gap={16} mb={32}>
				<FormTitle>Mi Portal Clínicas</FormTitle>
				<ClinicLogoContainer gap={8}>
					<ClinicLogo src={ClinicaSantaIsabel} alt="Clínica Santa Isabel" />
					<ClinicLogo src={ClinicaDelSol} alt="Clínica del Sol" />
					<ClinicLogo src={ClinicaBazterrica} alt="Clínica Bazterrica" />
					<ClinicLogo src={CentroMedicoBazterica} alt="Centro Médico Bazterrica" />
					<ClinicLogo src={CentroMedicoSantaRita} alt="Centro Médico Santa Rita" />
					<ClinicLogo src={CentroMedicoSantaIsabel} alt="Centro Médico Santa Isabel" />
				</ClinicLogoContainer>
			</FormHeader>

			<FeedbackContent>
				{status === 'success' && (
					<Feedback
						title="Login realizado con éxito"
						bodyText={'Estás ingresando a tu cuenta de Portal Clínicas.'}
						icon={<CheckCircle size={80} />}
						isSucces={true}
						haveButton={false}
						textGap={10}
					/>
				)}
				{status === 'processing' && (
					<Feedback
						title="Procesando..."
						bodyText="Estamos validando tu acceso."
						icon={<Spinner />}
						isSucces={false}
						haveButton={false}
						textGap={10}
					/>
				)}
				{status === 'error' && (
					<Feedback
						title="Ocurrió un error"
						bodyText={'No se pudo validar el inicio de sesión.'}
						icon={<Info size={80} />}
						isSucces={false}
						isError={true}
						haveButton={false}
						textGap={10}
					/>
				)}
			</FeedbackContent>
		</FormContainer>
	)
}

const mapDispatchToProps = {
	loginAction,
	setPatientDetails
}

export default connect(null, mapDispatchToProps)(RedirectPage)
